import { useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import Logo from '../../components/Logo.jsx'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'
import ClientTargetField from '../../components/ClientTargetField.jsx'
import { defaultTarget, targetClients, targetError, targetPayload } from '../../lib/clientTarget.js'

/**
 * Picker pós-OAuth Meta (etapa de confirmação, página focada / sem sidebar).
 *
 * Recebe via location.state a lista de ad accounts autorizadas (connectionId +
 * accounts, vindos do callback). O cliente é o dono das contas (#351): o usuário
 * escolhe, por conta, um cliente existente ou um novo; várias contas podem ir para
 * o mesmo cliente. Conta já vinculada fica no cliente atual.
 *
 * Nada é persistido até "Salvar": ali o backend vincula meta_ad_accounts ao cliente
 * escolhido (criando-o se for novo) + enfileira a coleta inicial, atômico. Espelha
 * GoogleAdsAccountPicker.
 */
const ACCOUNT_STATUS_LABEL = {
  1: null,            // ACTIVE — sem badge
  2: 'Desativada',
  3: 'Não paga',
  7: 'Em análise',
  8: 'Pendente',
  9: 'Período de graça',
  100: 'Fechando',
  101: 'Fechada',
}

export default function MetaAdsAccountPicker() {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast, refreshAccess, availableClients, activeOrganizationId, selectedClient } = useApp()
  const data = location.state

  if (!data?.connectionId || !Array.isArray(data?.accounts)) {
    return <Navigate to="/perfil" replace />
  }

  const clients = useMemo(
    () => targetClients(availableClients, activeOrganizationId),
    [availableClients, activeOrganizationId]
  )

  // Estado por conta: { checked, client, newName, domain }. O usuário escolhe quais contas vincular:
  // entram marcadas só as já vinculadas (reconexão: atualiza a conexão delas); o resto, desmarcado.
  const [rows, setRows] = useState(() => {
    const initial = {}
    for (const a of data.accounts) {
      initial[a.meta_account_id] = {
        checked: !!a.linked_client_id,
        client: defaultTarget(a.linked_client_id, clients, selectedClient?.id),
        newName: a.name || '',
        domain: '',
      }
    }
    return initial
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const toggle = (id) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], checked: !prev[id].checked } }))

  const patchRow = (id, patch) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))

  const accountNameOf = (a) => a.name || `Conta ${a.account_id || a.meta_account_id}`

  const canSubmit = useMemo(() => {
    const checked = data.accounts.filter((a) => rows[a.meta_account_id]?.checked)
    return (
      checked.length > 0 &&
      checked.every((a) => !targetError(rows[a.meta_account_id], clients))
    )
  }, [data.accounts, rows, clients])

  async function handleSubmit() {
    setError('')
    const checked = data.accounts.filter((a) => rows[a.meta_account_id]?.checked)

    if (checked.length === 0) {
      setError('Selecione pelo menos uma conta para vincular.')
      return
    }
    const invalid = checked.map((a) => targetError(rows[a.meta_account_id], clients)).find(Boolean)
    if (invalid) {
      setError(invalid)
      return
    }

    let accounts
    try {
      accounts = checked.map((a) => ({
        meta_account_id: a.meta_account_id,
        account_name: accountNameOf(a),
        ...targetPayload(rows[a.meta_account_id], clients),
        business_id: a.business_id ?? null,
        currency: a.currency ?? null,
        timezone: a.timezone ?? null,
        account_status: a.account_status ?? null,
        disable_reason: a.disable_reason ?? null,
        spend_cap: a.spend_cap ?? null,
        amount_spent: a.amount_spent ?? null,
      }))
    } catch (err) {
      setError(err.message)
      return
    }

    try {
      setSubmitting(true)
      const res = await api.linkMetaAdsAccounts({
        connectionId: data.connectionId,
        accounts,
      })
      await refreshAccess?.()
      toast(
        `${res.meta_ad_account_ids.length} conta(s) Meta Ads vinculada(s) com sucesso.`,
        'success'
      )
      navigate(data.returnTo || '/google-ads', { replace: true })
    } catch (err) {
      setError(err.message || 'Erro ao vincular contas.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="ob-shell">
      <div className="ob-topbar">
        <div className="ob-logo"><Logo height={16} /></div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate(data.returnTo || '/perfil')}
          disabled={submitting}
        >
          Cancelar
        </button>
      </div>

      <div className="ob-body">
        <div className="ob-card" style={{ maxWidth: 640, width: '100%' }}>
          <div className="ob-badge">Passo 2 de 2</div>
          <h1 className="ob-title">Confirme suas contas Meta Ads</h1>
          <p className="ob-desc">
            Escolha a qual cliente cada conta pertence. Várias contas podem ficar
            no mesmo cliente. Salve para concluir.
            <br />
            <small style={{ color: 'var(--ter)' }}>
              Conectado como {data.metaUserEmail || 'usuário Meta'}
            </small>
          </p>

          {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}

          {data.accounts.length === 0 ? (
            <div className="empty">
              <div className="empty-desc">
                Nenhuma conta de anúncio acessível por este usuário Meta. Verifique
                as permissões no Business Manager e tente novamente.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {data.accounts.map((a) => {
                const row = rows[a.meta_account_id] || {}
                const statusLabel = ACCOUNT_STATUS_LABEL[a.account_status]
                return (
                  <div
                    key={a.meta_account_id}
                    className="section-card"
                    style={{
                      padding: 16,
                      opacity: row.checked ? 1 : 0.55,
                      transition: 'opacity .15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <input
                        type="checkbox"
                        checked={!!row.checked}
                        onChange={() => toggle(a.meta_account_id)}
                        style={{ width: 16, height: 16, marginTop: 4 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="conn-name">
                          {accountNameOf(a)}
                          {statusLabel && (
                            <span className="badge badge-amber" style={{ marginLeft: 8 }}>
                              {statusLabel}
                            </span>
                          )}
                        </div>
                        <div className="conn-meta" style={{ marginBottom: 10 }}>
                          {a.meta_account_id}
                          {a.currency ? ` · ${a.currency}` : ''}
                          {a.timezone ? ` · ${a.timezone}` : ''}
                        </div>
                        <ClientTargetField
                          row={row}
                          clients={clients}
                          linkedClientId={a.linked_client_id}
                          disabled={!row.checked}
                          onChange={(patch) => patchRow(a.meta_account_id, patch)}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <button
            className="btn btn-black btn-full"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            style={{ marginTop: 20 }}
          >
            {submitting ? 'Salvando…' : 'Salvar e concluir'}
          </button>
        </div>
      </div>
    </div>
  )
}
